import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, selectAtom, unwrap } from 'jotai/utils';
import {
  progAtom,
  progIdAtom,
  useProgrammationId,
} from 'containers/Programmations/atoms/progAtoms';
import { matieresAtom } from 'containers/Programmations/atoms/matieresAtoms';
import { Matiere, Programmation } from '@teetsh/app/src/types/programmations';
import { Color } from '@teetsh/app/src/types/colors';
import { TFunction } from 'i18next';
import { PrimitiveAtom } from 'jotai/index';
import swap from '@teetsh/app/src/utils/arrays/swap';
import { useTranslation } from 'react-i18next';
import { reorderDomainesWithHistoryAtom } from 'containers/Programmations/atoms/useReorderDomaines';
import { GroupCol, GroupRow } from './types';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

export interface DisplayConfig {
  groupRow: GroupRow;
  groupCol: GroupCol;
  hiddenPeriodes: string[];
  hiddenDomaines: string[];
}

const progDisplayConfigAtom = atomWithStorage<Record<string, DisplayConfig>>(
  'progDisplayConfig',
  {},
);

const displayConfigWithDefaultAtom = atom(get => {
  const progId = get(progIdAtom);
  const progDisplayConfig = get(progDisplayConfigAtom);
  return (
    progDisplayConfig[progId] || {
      groupRow: 'domaines',
      groupCol: 'periodes',
      hiddenPeriodes: [],
      hiddenDomaines: [],
    }
  );
});

export const useProgDisplayConfig = () =>
  useAtomValue(displayConfigWithDefaultAtom);

export const useIsProgDisplayConfigPersisted = () => {
  const progId = useProgrammationId();
  const configObject = JSON.parse(
    localStorage.getItem('progDisplayConfig') || '{}',
  );
  return !!configObject[progId];
};

const updateProgDisplayConfigAtom = atom(
  null,
  (get, set, arg: DisplayConfig) => {
    const progId = get(progIdAtom);
    const progDisplayConfig = get(progDisplayConfigAtom);
    const configOrDefault = get(displayConfigWithDefaultAtom);
    if (arg.groupRow === arg.groupCol) {
      if (arg.groupRow !== configOrDefault.groupRow) {
        return set(progDisplayConfigAtom, {
          ...progDisplayConfig,
          [progId]: {
            ...arg,
            groupCol: configOrDefault.groupRow,
          },
        });
      }
      if (arg.groupCol !== configOrDefault.groupCol) {
        return set(progDisplayConfigAtom, {
          ...progDisplayConfig,
          [progId]: {
            ...arg,
            groupRow: configOrDefault.groupCol,
          },
        });
      }
    }
    return set(progDisplayConfigAtom, {
      ...progDisplayConfig,
      [progId]: arg,
    });
  },
);

export const useSetProgDisplayConfig = () =>
  useSetAtom(updateProgDisplayConfigAtom);

const toggleHiddenPeriodesAtom = atom(null, (get, set, id: string) => {
  const progId = get(progIdAtom);
  const progDisplayConfig = get(progDisplayConfigAtom);
  const configOrDefault = get(displayConfigWithDefaultAtom);

  if (configOrDefault.hiddenPeriodes.includes(id)) {
    set(progDisplayConfigAtom, {
      ...progDisplayConfig,
      [progId]: {
        ...configOrDefault,
        hiddenPeriodes: configOrDefault.hiddenPeriodes.filter(p => p !== id),
      },
    });
  } else {
    set(progDisplayConfigAtom, {
      ...progDisplayConfig,
      [progId]: {
        ...configOrDefault,
        hiddenPeriodes: [...configOrDefault.hiddenPeriodes, id],
      },
    });
  }
});

export const useToggleHiddenPeriodes = () =>
  useSetAtom(toggleHiddenPeriodesAtom);

const toggleHiddenDomainesAtom = atom(null, (get, set, id: string) => {
  const progId = get(progIdAtom);
  const progDisplayConfig = get(progDisplayConfigAtom);
  const configOrDefault = get(displayConfigWithDefaultAtom);
  if (configOrDefault.hiddenDomaines.includes(id)) {
    set(progDisplayConfigAtom, {
      ...progDisplayConfig,
      [progId]: {
        ...configOrDefault,
        hiddenDomaines: configOrDefault.hiddenDomaines.filter(p => p !== id),
      },
    });
  } else {
    set(progDisplayConfigAtom, {
      ...progDisplayConfig,
      [progId]: {
        ...configOrDefault,
        hiddenDomaines: [...configOrDefault.hiddenDomaines, id],
      },
    });
  }
});

export const nbOfDomainesAtom = selectAtom<Programmation | undefined, number>(
  unwrap(progAtom),
  (prog, prev) => {
    if (!prog) {
      return prev || 0;
    }
    return (prog?.matieres || []).reduce(
      (acc, mat) => acc + (mat.domaines || []).length,
      0,
    );
  },
);

export const useNbOfDomaines = () => useAtomValue(nbOfDomainesAtom);

export const matiereIdAtom = atom<string | undefined>(undefined);
export const useToggleHiddenDomaines = () =>
  useSetAtom(toggleHiddenDomainesAtom);

const domainesOfMatiereAtom = atom(get => {
  const matiereId = get(matiereIdAtom);
  const prog = get(unwrap(progAtom));
  if (!prog || !matiereId) {
    return 0;
  }
  const matiere = prog.matieres.find(m => m.id === matiereId);
  return matiere?.domaines;
});

const nbOfDomainesForMatiereAtom = selectAtom(
  domainesOfMatiereAtom,
  domaines => (domaines || [])?.length || 0,
);

export const useNbOfDomainesForMatiere = () =>
  useAtomValue(nbOfDomainesForMatiereAtom);

export const useMatiereId = () => useAtomValue(matiereIdAtom);

export const matiereWithDomainesInfoAtom = atom(get => {
  const matieres = get(matieresAtom);
  const matiereId = get(matiereIdAtom);
  return matieres.find(m => m.id === matiereId);
});

const matieresOnlyAtom = selectAtom<
  Matiere[] | undefined,
  Array<{ name: string; id: string; color: Color | TagColorWithIntensity }>
>(
  matieresAtom,
  matieres =>
    (matieres || []).map(p => ({
      name: p.name,
      id: p.id,
      color: p.color,
    })),
  (a, b) => JSON.stringify(a) === JSON.stringify(b),
);

export const useMatieresOnly = () => useAtomValue(matieresOnlyAtom);

export const reorderSingleDomaineAtom = (t: TFunction<['programmation']>) =>
  atom<
    any,
    [
      {
        matiereAtom?: PrimitiveAtom<Matiere>;
        domaineId: string;
        dir: -1 | 1;
      },
    ],
    any
  >(null, async (_get, set, { domaineId, matiereAtom, dir }) => {
    const matiere = matiereAtom
      ? _get(matiereAtom)
      : _get(matiereWithDomainesInfoAtom);
    const domaineIds = matiere?.domaines.map(d => d.id) || [];
    const newDomaineIds = swap(domaineIds, domaineIds.indexOf(domaineId), dir);
    return set(reorderDomainesWithHistoryAtom(t), {
      domainesIds: newDomaineIds,
      matiereId: matiere?.id,
    });
  });

export const useReorderSingleDomaine = () => {
  const { t } = useTranslation(['programmation']);
  return useSetAtom(reorderSingleDomaineAtom(t));
};

const hiddenDomainesIdOfMatiereAtom = atom<string[]>(get => {
  const progId = get(progIdAtom);
  const domainesOfMatiere = get(domainesOfMatiereAtom);
  const displayConfigs = get(progDisplayConfigAtom);
  return (domainesOfMatiere || [])
    .filter(d => (displayConfigs[progId]?.hiddenDomaines || []).includes(d.id))
    .map(d => d.id);
});

export const useHiddenDomainesIdOfMatiere = () =>
  useAtomValue(hiddenDomainesIdOfMatiereAtom);

const allHiddenDomainesAtom = atom<string[]>(get => {
  const progId = get(progIdAtom);
  const displayConfigs = get(progDisplayConfigAtom);
  return Object.values(displayConfigs[progId]?.hiddenDomaines || []).flat();
});

export const useAllHiddenDomaines = () => useAtomValue(allHiddenDomainesAtom);
